import React from "react";
import { Box } from "@mui/joy";
import ColorPalette from "./ColorPalette";
import IconButtonCTA from "./IconButtonCTA";
import ActionTooltip from "./ActionTooltip";
import {
  ChatBubbleOutlineIcon,
  SDelete,
  SchoolOutlinedIcon,
  SOpenProfile,
} from "../../styles/StyledMUI";
import CommentsModal from "./CommentsModal";
import { useConfirmationModal } from "../../contexts/app/confirmation";
import { useAuth } from "../../contexts/api";
import type { MultiComment } from "../../api/zekay/comments";
import {
  colorTokens,
  emojiFontFamily,
  excursionCommentIconEmptyPlainSx,
  withAlpha,
} from "../../styles/globalStyles";
import { COLOR_PALETTE_GROUP, GROUP_DEFAULT_COLOR } from "../../utils/colorPalette";
import { userDisplayName } from "../../utils/userHelper";
import ExcursionClientEmojiPicker from "../excursions/shared/display/ExcursionClientEmojiPicker";

const COLOR_SWATCH_SIZE = 14;

export type PersonChipBookingStatus = "pending" | "validated" | "rejected" | "cancelled";

const BOOKING_STATUS_TOOLTIP_FR: Record<PersonChipBookingStatus, string> = {
  validated: "Prestation validée",
  pending: "En attente de validation",
  rejected: "Prestation refusée",
  cancelled: "Prestation annulée",
};

const BOOKING_STATUS_SYMBOL: Record<PersonChipBookingStatus, string> = {
  validated: "✅",
  pending: "⏳",
  rejected: "❌",
  cancelled: "⛔",
};

export type PersonChipColorPicker = {
  value: string;
  onChange: (hex: string) => void;
  ariaLabel?: string;
};

export type PersonChipProps = {
  lastName?: string | null;
  firstName?: string | null;
  qualification?: string | null;
  label?: string;
  children?: React.ReactNode;
  chipColor?: string | null;
  onRemove?: () => void;
  confirmRemove?: { title: string; message: string };
  colorPicker?: PersonChipColorPicker;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  size?: "sm" | "md" | "lg";
  variant?: "solid" | "soft" | "outlined" | "plain";
  bookingStatus?: PersonChipBookingStatus | null;
  comments?: MultiComment[];
  /** When true (e.g. excursion card tooltip): show only Important comment in tooltip — no CommentsModal. */
  commentsImportantTooltipOnly?: boolean;
  onCommentsChange?: (comments: MultiComment[]) => void;
  onCommentsModalClosed?: () => void;
  emoji?: string | null;
  emojiPicker?: {
    value: string | null | undefined;
    onChange: (emoji: string | undefined) => void;
    disabled?: boolean;
  };
  inProgressTrainingsTooltip?: string | null;
  /** When set with `onOpenCustomerOverview`, opens the shared customer modal (liste client). */
  customerId?: number | null;
  onOpenCustomerOverview?: () => void;
  sx?: object;
};

function buildLabel(props: {
  lastName?: string | null;
  firstName?: string | null;
  qualification?: string | null;
  label?: string;
}): string {
  const qual = props.qualification?.trim();
  if (props.label != null && props.label !== "") {
    return qual ? `${props.label} - ${qual}` : props.label;
  }
  const first = props.firstName?.trim();
  const last = props.lastName?.trim();
  const name = [first, last].filter(Boolean).join(" ");
  if (!name && !qual) return "—";
  return qual ? `${name} - ${qual}` : name;
}

function ColorSwatchDot({ hex, size = COLOR_SWATCH_SIZE }: { hex: string; size?: number }) {
  const safeHex = hex && String(hex).startsWith("#") ? hex : GROUP_DEFAULT_COLOR;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: `1px solid ${withAlpha(colorTokens.text.primary, 0.5)}`,
        flexShrink: 0,
        backgroundColor: safeHex,
      }}
    />
  );
}

export default function PersonChip({
  lastName,
  firstName,
  qualification,
  label,
  children,
  chipColor,
  onRemove,
  confirmRemove,
  colorPicker,
  draggable,
  onDragStart,
  onDragEnd,
  size = "sm",
  variant = "soft",
  bookingStatus,
  comments,
  commentsImportantTooltipOnly = false,
  onCommentsChange,
  onCommentsModalClosed,
  emoji,
  emojiPicker,
  inProgressTrainingsTooltip,
  customerId: customerIdForOverview,
  onOpenCustomerOverview,
  sx = {},
}: PersonChipProps) {
  const { showConfirmation } = useConfirmationModal();
  const { user } = useAuth();
  const displayLabel = children ?? buildLabel({ lastName, firstName, qualification, label });
  const [commentsOpen, setCommentsOpen] = React.useState(false);

  const paddingY = size === "sm" ? 0.25 : size === "md" ? 0.5 : 0.75;
  const paddingX = size === "sm" ? 0.75 : size === "md" ? 1 : 1.25;
  const fontSize =
    size === "sm"
      ? { xs: "0.7rem", sm: "0.75rem" }
      : size === "md"
        ? { xs: "0.8rem", sm: "0.875rem" }
        : { xs: "0.875rem", sm: "1rem" };

  const baseChipSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    borderRadius: "md",
    backgroundColor: colorTokens.bg.surfaceMuted,
    color: colorTokens.text.primary,
    minHeight: 24,
    maxWidth: "100%",
    ...(typeof sx === "object" && sx),
  };

  const leftSlotWrapperSx = {
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    alignSelf: "stretch",
    "& > *": { verticalAlign: "middle" },
  };

  const leftSlot =
    colorPicker != null ? (
      <Box component="span" sx={leftSlotWrapperSx} onClick={(e) => e.stopPropagation()}>
        <ColorPalette
          mode="chip"
          value={colorPicker.value}
          onChange={colorPicker.onChange}
          palette={COLOR_PALETTE_GROUP}
          defaultColor={GROUP_DEFAULT_COLOR}
          menuZIndex={2200}
          aria-label={colorPicker.ariaLabel ?? "Choisir une couleur"}
        />
      </Box>
    ) : chipColor != null && String(chipColor).trim() ? (
      <Box component="span" sx={leftSlotWrapperSx}>
        <ColorSwatchDot hex={chipColor} />
      </Box>
    ) : null;

  const emojiSlot =
    emojiPicker != null ? (
      <Box component="span" sx={leftSlotWrapperSx} onClick={(e) => e.stopPropagation()}>
        <ExcursionClientEmojiPicker
          value={emojiPicker.value}
          onChange={emojiPicker.onChange}
          disabled={emojiPicker.disabled}
        />
      </Box>
    ) : emoji != null && String(emoji).trim() ? (
      <Box
        component="span"
        title="Mood"
        sx={{
          flexShrink: 0,
          fontSize: "1.1rem",
          lineHeight: 1,
          px: 0.15,
          userSelect: "none",
          fontFamily: emojiFontFamily,
        }}
      >
        {String(emoji).trim()}
      </Box>
    ) : null;

  const hasTrainingHint =
    typeof inProgressTrainingsTooltip === "string" && inProgressTrainingsTooltip.trim().length > 0;

  const trainingSessionsSlot = hasTrainingHint ? (
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        pl: 0.5,
        pr: 0.5,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <ActionTooltip title={inProgressTrainingsTooltip!.trim()}>
        <Box
          component="span"
          role="img"
          aria-label={inProgressTrainingsTooltip!.trim()}
          sx={{ display: "inline-flex", lineHeight: 0, cursor: "default" }}
        >
          <SchoolOutlinedIcon
            sx={{ fontSize: "0.95rem", color: colorTokens.status.success }}
            aria-hidden
          />
        </Box>
      </ActionTooltip>
    </Box>
  ) : null;

  const overviewSlot =
    customerIdForOverview != null && typeof onOpenCustomerOverview === "function" ? (
      <Box
        component="span"
        sx={{ flexShrink: 0, display: "inline-flex", alignItems: "center", pl: 0.25 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ActionTooltip title="Fiche client">
          <IconButtonCTA
            size={size === "lg" ? "md" : "sm"}
            variant="plain"
            aria-label="Fiche client"
            ctaVariant="accent"
            onClick={(e) => {
              e.stopPropagation();
              onOpenCustomerOverview();
            }}
            sx={{
              "--IconButton-size": size === "lg" ? "22px" : size === "md" ? "20px" : "18px",
            }}
          >
            <SOpenProfile sx={{ fontSize: size === "sm" ? "0.92rem" : "1rem" }} />
          </IconButtonCTA>
        </ActionTooltip>
      </Box>
    ) : null;

  const dragZone = (
    <Box
      component="span"
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      sx={{
        flex: "1 1 auto",
        minWidth: 0,
        py: paddingY,
        px: paddingX,
        fontSize,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...(draggable && { cursor: "grab", "&:active": { cursor: "grabbing" } }),
      }}
    >
      {displayLabel}
    </Box>
  );

  const handleRemoveClick = React.useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (confirmRemove) {
        const ok = await showConfirmation(confirmRemove.title, confirmRemove.message);
        if (!ok) return;
      }
      onRemove?.();
    },
    [onRemove, confirmRemove, showConfirmation],
  );

  const handleCommentsClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCommentsOpen(true);
  }, []);

  const importantComments =
    commentsImportantTooltipOnly && comments?.length
      ? comments.filter((c) => c.Important === true && (c.Comment?.trim() ?? "").length > 0)
      : [];

  const importantCommentsTooltipText =
    importantComments.length > 0
      ? importantComments.map((c) => c.Comment.trim()).join("\n\n")
      : null;

  const bookingBadge =
    bookingStatus != null ? (
      <Box
        component="span"
        sx={{ flexShrink: 0, display: "inline-flex", alignItems: "center" }}
        onClick={(e) => e.stopPropagation()}
      >
        <ActionTooltip title={BOOKING_STATUS_TOOLTIP_FR[bookingStatus]}>
          <Box
            component="span"
            role="img"
            aria-label={BOOKING_STATUS_TOOLTIP_FR[bookingStatus]}
            sx={{ fontSize: "0.85em", lineHeight: 1, mr: 0.25, cursor: "default" }}
          >
            {BOOKING_STATUS_SYMBOL[bookingStatus]}
          </Box>
        </ActionTooltip>
      </Box>
    ) : null;

  const hasComments = (comments?.length ?? 0) > 0;

  const commentsTooltipTitle =
    comments && comments.length > 0
      ? comments
          .map((c) => c.Comment)
          .filter(Boolean)
          .join(" | ")
      : "Aucun commentaire";

  const rightSlot = commentsImportantTooltipOnly ? (
    importantCommentsTooltipText != null ? (
      <Box
        component="span"
        sx={{ flexShrink: 0, display: "inline-flex", alignItems: "center" }}
        onClick={(e) => e.stopPropagation()}
      >
        <ActionTooltip
          title={
            <Box
              sx={{ whiteSpace: "pre-wrap", maxWidth: { xs: "70vw", sm: 320 }, textAlign: "left" }}
            >
              {importantCommentsTooltipText}
            </Box>
          }
        >
          <Box
            component="span"
            role="img"
            aria-label={importantCommentsTooltipText}
            sx={{ display: "inline-flex", lineHeight: 0, cursor: "default" }}
          >
            <ChatBubbleOutlineIcon
              sx={{
                fontSize: "0.9rem",
                color: `${colorTokens.status.success} !important`,
              }}
              aria-hidden
            />
          </Box>
        </ActionTooltip>
      </Box>
    ) : null
  ) : onCommentsChange != null || hasComments ? (
    <Box
      component="span"
      sx={{ flexShrink: 0, display: "inline-flex", alignItems: "center" }}
      onClick={(e) => e.stopPropagation()}
    >
      <ActionTooltip title={commentsTooltipTitle}>
        <IconButtonCTA
          size="sm"
          variant="plain"
          aria-label="Commentaires"
          ctaVariant={hasComments ? "success" : "accent"}
          onClick={handleCommentsClick}
          sx={{
            "--IconButton-size": "20px",
            ...(hasComments ? {} : excursionCommentIconEmptyPlainSx),
          }}
        >
          <ChatBubbleOutlineIcon
            sx={{
              fontSize: "0.9rem",
              color: hasComments
                ? `${colorTokens.status.success} !important`
                : `${colorTokens.text.muted} !important`,
            }}
          />
        </IconButtonCTA>
      </ActionTooltip>
    </Box>
  ) : null;

  const removeSlot =
    onRemove != null ? (
      <Box
        component="span"
        sx={{ flexShrink: 0, display: "inline-flex", alignItems: "center" }}
        onClick={(e) => e.stopPropagation()}
      >
        <IconButtonCTA
          size="sm"
          variant="plain"
          aria-label="Retirer"
          ctaVariant="danger"
          onClick={handleRemoveClick}
          sx={{ "--IconButton-size": "20px" }}
        >
          <SDelete iconColor="red" />
        </IconButtonCTA>
      </Box>
    ) : null;

  return (
    <Box sx={baseChipSx} component="span">
      {leftSlot}
      {emojiSlot}
      {dragZone}
      {overviewSlot}
      {trainingSessionsSlot}
      {bookingBadge}
      {rightSlot}
      {removeSlot}
      {!commentsImportantTooltipOnly ? (
        <CommentsModal
          open={commentsOpen}
          title={`Commentaires — ${displayLabel}`}
          currentUserName={user ? userDisplayName(user) : undefined}
          multiComments={comments}
          setMultiComments={undefined}
          onSave={(updated) => {
            if (onCommentsChange) onCommentsChange(updated ?? []);
          }}
          onClose={() => {
            setCommentsOpen(false);
            onCommentsModalClosed?.();
          }}
          allowEdit={onCommentsChange != null}
          showPdfSheetOption={onCommentsChange != null}
        />
      ) : null}
    </Box>
  );
}
